import { useEffect, useId, useState, type FormEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { Bug, Check, Lightbulb, MessageCircle, Wrench, type LucideIcon } from 'lucide-react'
import { productFeedbackApi, type FeedbackCategory } from '../../api/productFeedback'
import { useToast } from '../../components/ui/Toast'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/Dialog'
import { FormActions, FormField } from '../../components/ui/FormField'
import { UnsavedChangesDialog } from '../../components/ui/UnsavedChangesDialog'
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesWarning'
import { getApiErrorMessage } from '../../lib/api-errors'
import { cn } from '../../lib/utils'
import { useUserContext } from '../../providers/UserContextProvider'
import { useAuth } from '../../auth/AuthProvider'

const TITLE_MAX = 200
const DESCRIPTION_MAX = 5000

const CATEGORIES: Array<{ value: FeedbackCategory; icon: LucideIcon }> = [
  { value: 'BUG', icon: Bug },
  { value: 'FEATURE_REQUEST', icon: Lightbulb },
  { value: 'IMPROVEMENT', icon: Wrench },
  { value: 'OTHER', icon: MessageCircle },
]

interface FeedbackDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Feedback about the product itself, sent to the platform's makers rather than to a workspace admin.
 * Identified by default, because a reply needs someone to reply to. The switch says plainly what
 * going anonymous costs, and the dialog names everything it attaches on the reporter's behalf.
 * There is deliberately no attachment input.
 */
export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const { t, i18n } = useTranslation()
  const { toast } = useToast()
  const { pathname } = useLocation()
  const { user } = useAuth()
  const { userDetail } = useUserContext()
  const ids = useId()

  const [category, setCategory] = useState<FeedbackCategory>('IMPROVEMENT')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [anonymous, setAnonymous] = useState(false)

  useEffect(() => {
    if (open) {
      setCategory('IMPROVEMENT')
      setTitle('')
      setDescription('')
      setAnonymous(false)
    }
  }, [open])

  const reporterName = userDetail?.fullName || user?.name || user?.email || ''
  const trimmedTitle = title.trim()
  const trimmedDescription = description.trim()
  const canSubmit = trimmedTitle.length > 0 && trimmedDescription.length > 0
  const dirty = title !== '' || description !== ''

  const mutation = useMutation({
    mutationFn: () =>
      productFeedbackApi.submit({
        category,
        title: trimmedTitle,
        description: trimmedDescription,
        anonymous,
        // The path only. A query string can carry tokens and the server drops it anyway.
        context: {
          route: pathname,
          locale: i18n.language,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
        },
      }),
    onSuccess: () => {
      toast({ title: t('feedback.sent'), description: t('feedback.sentDescription'), variant: 'success' })
      onOpenChange(false)
    },
    onError: (error) => {
      toast({ title: t('feedback.failed'), description: getApiErrorMessage(error) ?? t('feedback.failedHint'), variant: 'error' })
    },
  })

  const closeGuard = useUnsavedChangesGuard({ isDirty: open && dirty, isSubmitting: mutation.isPending, onOpenChange })

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (canSubmit && !mutation.isPending) mutation.mutate()
  }

  const switchLabelId = `${ids}-anonymous-label`
  const switchNoteId = `${ids}-anonymous-note`

  return (
    <>
      <Dialog open={open} onOpenChange={closeGuard.onOpenChange}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('feedback.title')}</DialogTitle>
            <DialogDescription>{t('feedback.description')}</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <fieldset>
              <legend className="mb-1.5 block text-sm font-medium text-foreground">{t('feedback.categoryLabel')}</legend>
              <div className="grid grid-cols-2 gap-2">
                {CATEGORIES.map(({ value, icon: Icon }) => {
                  const selected = category === value
                  return (
                    <label key={value} className="relative block cursor-pointer">
                      <input
                        type="radio"
                        name={`${ids}-category`}
                        value={value}
                        checked={selected}
                        onChange={() => setCategory(value)}
                        className="peer sr-only"
                      />
                      <span
                        className={cn(
                          'flex min-h-11 items-center gap-2.5 rounded-md border px-3 py-2 text-sm transition-colors',
                          'peer-focus-visible:ring-2 peer-focus-visible:ring-primary/60',
                          selected
                            ? 'border-primary bg-primary/10 font-semibold text-foreground ring-1 ring-inset ring-primary/60'
                            : 'border-input bg-background font-medium text-muted-foreground hover:bg-accent hover:text-foreground'
                        )}
                      >
                        <Icon className={cn('hidden h-4 w-4 shrink-0 sm:block', selected && 'text-primary')} aria-hidden="true" />
                        <span className="min-w-0 flex-1 break-words leading-tight">{t(`feedback.category.${value}`)}</span>
                        {selected && <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />}
                      </span>
                    </label>
                  )
                })}
              </div>
            </fieldset>

            <FormField id={`${ids}-title`} label={t('feedback.titleLabel')} required>
              {({ fieldId, describedBy, invalid }) => (
                <input
                  id={fieldId}
                  type="text"
                  className="input-field"
                  value={title}
                  maxLength={TITLE_MAX}
                  placeholder={t('feedback.titlePlaceholder')}
                  autoComplete="off"
                  onChange={(e) => setTitle(e.target.value)}
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                />
              )}
            </FormField>

            <FormField id={`${ids}-description`} label={t('feedback.descriptionLabel')} required>
              {({ fieldId, describedBy, invalid }) => (
                <>
                  <textarea
                    id={fieldId}
                    className="input-field min-h-32 resize-y"
                    rows={5}
                    value={description}
                    maxLength={DESCRIPTION_MAX}
                    placeholder={t('feedback.descriptionPlaceholder')}
                    onChange={(e) => setDescription(e.target.value)}
                    aria-describedby={describedBy}
                    aria-invalid={invalid}
                  />
                  <p className="text-right text-xs tabular-nums text-muted-foreground" aria-hidden="true">
                    {description.length} / {DESCRIPTION_MAX}
                  </p>
                </>
              )}
            </FormField>

            <div
              className={cn(
                'rounded-md border px-3 py-3 transition-colors',
                anonymous ? 'border-primary/40 bg-primary/5' : 'border-border bg-muted/40'
              )}
            >
              <div className="flex items-center justify-between gap-3">
                <span id={switchLabelId} className="text-sm font-medium text-foreground">
                  {t('feedback.anonymousLabel')}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={anonymous}
                  aria-labelledby={switchLabelId}
                  aria-describedby={switchNoteId}
                  onClick={() => setAnonymous((value) => !value)}
                  className={cn(
                    'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                    anonymous ? 'border-primary bg-primary' : 'border-muted-foreground/50 bg-muted'
                  )}
                >
                  <span
                    className={cn(
                      'inline-flex h-4 w-4 items-center justify-center rounded-full bg-background shadow-sm transition-transform',
                      anonymous ? 'translate-x-6' : 'translate-x-1'
                    )}
                  >
                    {anonymous && <Check className="h-3 w-3 text-primary" aria-hidden="true" />}
                  </span>
                </button>
              </div>
              <p id={switchNoteId} className="mt-1.5 text-xs leading-5 text-muted-foreground" aria-live="polite">
                {anonymous ? t('feedback.anonymousNote') : t('feedback.identifiedNote', { name: reporterName })}
              </p>
            </div>

            <p className="text-xs leading-5 text-muted-foreground">{t('feedback.autoContext')}</p>

            <FormActions
              cancelLabel={t('common.cancel')}
              submitLabel={mutation.isPending ? t('feedback.sending') : t('feedback.submit')}
              onCancel={() => closeGuard.onOpenChange(false)}
              isSubmitting={mutation.isPending}
              submitDisabled={!canSubmit}
            />
          </form>
        </DialogContent>
      </Dialog>
      <UnsavedChangesDialog
        open={closeGuard.confirmOpen}
        onOpenChange={closeGuard.setConfirmOpen}
        title={t('common.unsavedChangesTitle')}
        description={t('common.unsavedChangesDescription')}
        keepEditingLabel={t('common.keepEditing')}
        discardLabel={t('common.discardChanges')}
        onKeepEditing={closeGuard.keepEditing}
        onDiscard={closeGuard.discardChanges}
      />
    </>
  )
}
